// =============================================================================
// useAggregations Hook
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import { useLogCollectorClient } from './useLogCollectorClient.js';
import type { AggregationConfig, AggregationConfigInput, AggregationFilters, AggregationResult } from '../lib/api.js';

export interface UseAggregationsOptions {
  /** App ID to fetch aggregations for */
  appId: string | null;
}

export interface UseAggregationsResult {
  /** Aggregation configs */
  configs: AggregationConfig[];
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Create a new aggregation config */
  create: (appId: string, config: AggregationConfigInput) => Promise<AggregationConfig | null>;
  /** Update an aggregation config */
  update: (appId: string, configId: string, config: Partial<AggregationConfigInput>) => Promise<AggregationConfig | null>;
  /** Delete an aggregation config */
  remove: (appId: string, configId: string) => Promise<boolean>;
  /** Compute aggregation results */
  compute: (appId: string, configId: string, from: string, to: string, filters?: AggregationFilters | null) => Promise<AggregationResult[]>;
  /** Refetch data */
  refetch: () => Promise<void>;
}

export function useAggregations({ appId }: UseAggregationsOptions): UseAggregationsResult {
  const { client } = useLogCollectorClient();
  const [configs, setConfigs] = useState<AggregationConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfigs = useCallback(async () => {
    if (!appId) {
      setConfigs([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await client.listAggregations(appId);
      setConfigs(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch aggregations';
      setError(message);
      setConfigs([]);
    } finally {
      setIsLoading(false);
    }
  }, [client, appId]);

  // Fetch configs when appId changes
  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const create = useCallback(
    async (targetAppId: string, config: AggregationConfigInput): Promise<AggregationConfig | null> => {
      try {
        const created = await client.createAggregation(targetAppId, config);
        if (targetAppId === appId) {
          setConfigs((prev) => [...prev, created]);
        }
        return created;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create aggregation';
        setError(message);
        return null;
      }
    },
    [client, appId]
  );

  const update = useCallback(
    async (
      targetAppId: string,
      configId: string,
      config: Partial<AggregationConfigInput>
    ): Promise<AggregationConfig | null> => {
      try {
        const updated = await client.updateAggregation(targetAppId, configId, config);
        if (targetAppId === appId) {
          setConfigs((prev) => prev.map((c) => (c.id === configId ? updated : c)));
        }
        return updated;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update aggregation';
        setError(message);
        return null;
      }
    },
    [client, appId]
  );

  const remove = useCallback(
    async (targetAppId: string, configId: string): Promise<boolean> => {
      try {
        await client.deleteAggregation(targetAppId, configId);
        if (targetAppId === appId) {
          setConfigs((prev) => prev.filter((c) => c.id !== configId));
        }
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete aggregation';
        setError(message);
        return false;
      }
    },
    [client, appId]
  );

  const compute = useCallback(
    async (
      targetAppId: string,
      configId: string,
      from: string,
      to: string,
      filters?: AggregationFilters | null
    ): Promise<AggregationResult[]> => {
      try {
        const response = await client.computeAggregation(targetAppId, configId, from, to, filters ?? undefined);
        return response.results;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to compute aggregation';
        setError(message);
        return [];
      }
    },
    [client]
  );

  return {
    configs,
    isLoading,
    error,
    create,
    update,
    remove,
    compute,
    refetch: fetchConfigs,
  };
}
